import dayjs from 'dayjs';
import BadgeCheckIcon from '@liquidweb-dev/undercurrent/icons/lucide/BadgeCheck';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Tooltip from '@liquidweb-dev/undercurrent/Tooltip';
import ControlPanelIcon from 'components/ControlPanelIcon';
import { managementMap } from 'components/DrawerReimage/helpers';
import BackupsChip from 'components/ServersList/BackupsChip';
import EnhancedSecurityChip from 'components/ServersList/EnhancedSecurityChip';
import ServerListDetailBox from 'components/ServersList/ServersListDetailBox';
import { AssetDetails } from 'modules/queries/asset/useDetails';
import { getControlPanelDisplayName } from 'modules/asset/helpers';
import { fromApiBoolean } from '@liquidweb-dev/club-sauce';
import Box from '@liquidweb-dev/undercurrent/Box';
import { getHasBackups } from 'modules/backups/helpers';
import ApplicationOrOS from './ApplicationOrOS';
import Label from './Label';
import DisplayType from './ServerDetailsDisplayType';
import Location from './ServerDetailsLocation';

const ManagementLevelServerListDetailBox = ({
	managementLevel,
}: {
	managementLevel?: string;
}) => {
	if (!managementLevel) return null;

	const managementInfo = managementMap[managementLevel || ''];
	const label = managementInfo?.label || managementLevel;
	const isFullyManaged = managementLevel === 'Fully-Managed';

	if (!managementInfo) return null;

	return (
		<ServerListDetailBox sx={{ minWidth: '40px' }}>
			<Tooltip
				title={managementInfo.tooltip.title}
				message={managementInfo.tooltip.description}
				placement="bottom"
				variant="fancy"
				arrow
			>
				<Stack
					direction="row"
					sx={{
						alignItems: 'center',
						gap: 50,
						minWidth: 0,
						overflow: 'hidden',
					}}
				>
					{isFullyManaged && (
						<BadgeCheckIcon
							sx={({ size, vars }) => ({
								height: size.xS,
								width: size.xS,
								flexShrink: 0,
								color: vars.palette.uc.highlight.text.main,
							})}
						/>
					)}
					<Label
						sx={({ vars }) => ({
							textDecoration: 'underline',
							textDecorationStyle: 'dotted',
							...(isFullyManaged && {
								color: vars.palette.uc.highlight.text.main,
							}),
						})}
					>
						{label}
					</Label>
				</Stack>
			</Tooltip>
		</ServerListDetailBox>
	);
};

const ControlPanelServerListDetailBox = ({
	controlPanel,
}: {
	controlPanel: string | undefined;
}) => {
	const displayName = getControlPanelDisplayName(controlPanel);
	if (!controlPanel || !displayName) return null;

	return (
		<ServerListDetailBox>
			<Tooltip title={displayName}>
				<Box sx={{ display: 'inline-flex', p: 50, m: -50 }}>
					<ControlPanelIcon
						controlPanel={controlPanel}
						sx={({ size }) => ({
							height: size.xS,
						})}
					/>
				</Box>
			</Tooltip>
		</ServerListDetailBox>
	);
};

export interface ServerDetailsProps {
	assetDetails: AssetDetails;
	variant: 'card' | 'row';
}

const ServerDetails = ({ assetDetails, variant }: ServerDetailsProps) => {
	const {
		associationDetails,
		backupsState,
		capabilities,
		controlPanel,
		country,
		displayType,
		domain,
		location,
		managementLevel,
		os: fallbackOs,
		template,
		templateApp,
		acronisStatus,
		uniq_id: uniqId,
		scheduledCancellationDate,
	} = assetDetails;

	const os = template?.os_name || fallbackOs;
	const canBackups = Boolean(
		backupsState?.canAcronis || backupsState?.canCloudBackup,
	);
	const hasBackups = getHasBackups(assetDetails);
	const securityAssociation = associationDetails?.find(
		(a) =>
			a.type === 'ServerSecurity.ThreatDown' ||
			a.type === 'ServerSecurity.Imunify360',
	);
	const hasEnhancedSecurity = Boolean(securityAssociation);
	const enhancedSecurityStatus = securityAssociation?.status;
	const canEnhancedSecurity =
		fromApiBoolean(capabilities?.canThreatDown) ||
		fromApiBoolean(capabilities?.canImunify360);

	const cancellationDate = scheduledCancellationDate?.DateTime;
	const isCancellationScheduled = cancellationDate
		? dayjs(cancellationDate).isSameOrAfter(dayjs(), 'day')
		: false;

	return (
		<>
			<ServerListDetailBox sx={{ minWidth: '40px' }}>
				<DisplayType displayType={displayType || ''} />
			</ServerListDetailBox>
			<ServerListDetailBox sx={{ minWidth: (theme) => theme.size.xS }}>
				<Location location={location || ''} country={country || ''} />
			</ServerListDetailBox>
			<ServerListDetailBox>
				<ApplicationOrOS os={os} application={templateApp} variant={variant} />
			</ServerListDetailBox>
			<ControlPanelServerListDetailBox controlPanel={controlPanel} />
			<ManagementLevelServerListDetailBox managementLevel={managementLevel} />
			{!isCancellationScheduled && (canBackups || canEnhancedSecurity) && (
				<Stack
					direction="row"
					sx={{
						gap: 50,
					}}
				>
					<BackupsChip
						uniqId={uniqId}
						displayType={displayType}
						canBackups={canBackups}
						hasBackups={hasBackups}
						acronisStatus={acronisStatus}
					/>

					<EnhancedSecurityChip
						uniqId={uniqId}
						hostname={domain}
						displayType={displayType}
						hasEnhancedSecurity={hasEnhancedSecurity}
						canEnhancedSecurity={canEnhancedSecurity}
						enhancedSecurityStatus={enhancedSecurityStatus}
					/>
				</Stack>
			)}
		</>
	);
};

export default ServerDetails;
